/**
 * 医師関連の型定義
 */

/**
 * 性別の列挙型
 */
export enum Gender {
  MALE = 'male',
  FEMALE = 'female'
}

/**
 * 稼働ステータスの列挙型
 */
export enum DoctorStatus {
  ACTIVE = '契約済み',
  TERMINATED = '解約済み'
}

/**
 * シフト優先度の列挙型
 */
export enum ShiftPriority {
  A = 'A',
  B = 'B',
  C = 'C'
}

/**
 * 対応可能業務の型定義
 */
export interface AvailableTask {
  /** 業務ID */
  id: number;
  
  /** 業務名 */
  name: string;
  
  /** バックエンドで使用される値 */
  value: string;
}

/**
 * 対応可能言語の型定義
 */
export interface AvailableLanguage {
  /** 言語ID */
  id: number;
  
  /** 言語名 */
  name: string;
  
  /** バックエンドで使用される値 */
  value: string;
}

/**
 * 医師データの型定義
 */
export interface Doctor {
  /** 医師ID（主キー） */
  id: number;
  
  /** 医師コード（一意コード） */
  doctor_code: string;
  
  /** 姓（苗字） */
  first_name: string;
  
  /** 名（名前） */
  last_name: string;
  
  /** 性別 */
  gender: Gender;
  
  /** 稼働ステータス */
  status: DoctorStatus;
  
  /** 稼働開始日 (yyyy/mm/dd) */
  start_date: string;
  
  /** 稼働解約日 (yyyy/mm/dd) */
  termination_date: string | null;
  
  /** 連絡用アドレス（配列またはセミコロン区切り） */
  contact_emails: string[] | string;

  /** 医師診療リストスプレッドシートURL */
  medical_spreadsheet_url?: string | null;
  
  /** 個人携帯番号（フロントエンド用） */
  personal_phone?: string;
  
  /** 社用携帯番号（フロントエンド用） */
  company_phone?: string;
  
  /** 個人携帯番号（APIレスポンス用） */
  personal_tel_number?: string;
  
  /** 社用携帯番号（APIレスポンス用） */
  company_tel_number?: string;
  
  /** シフト優先度 */
  shift_priority: ShiftPriority;
  
  /** 対応可能業務リスト（バックエンドからは文字列配列として受信） */
  supported_services?: string[];
  
  /** 対応可能言語リスト（バックエンドからは文字列配列として受信） */
  supported_languages?: string[];
  
  /** 作成日時 */
  created_at?: string;
  
  /** 更新日時 */
  updated_at?: string;
}

/**
 * 医師登録・更新用のフォームデータ型
 */
export interface DoctorFormData {
  doctor_code: string;
  first_name: string;
  last_name: string;
  gender: Gender;
  status: DoctorStatus;
  start_date: string;
  termination_date: string | null;
  medical_spreadsheet_url: string;
  contact_emails: string;
  personal_phone?: string;
  company_phone?: string;
  shift_priority: ShiftPriority;
  supported_services?: string[];
  supported_languages?: string[];
}

/**
 * 医師一覧取得APIのレスポンス型
 */
export interface DoctorListResponse {
  data: Doctor[];
  meta?: {
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
  };
}

/**
 * 医師詳細取得APIのレスポンス型
 */
export interface DoctorDetailResponse {
  data: Doctor;
}

/**
 * APIエラーレスポンス型
 */
export interface DoctorApiError {
  message: string;
  errors?: {
    [field: string]: string[];
  };
}

/**
 * 医師検索・フィルタリングパラメータ
 */
export interface DoctorSearchParams {
  /** 名前またはコードでの検索 */
  search?: string;
  
  /** ステータスでのフィルタリング */
  status?: DoctorStatus;
  
  /** 性別でのフィルタリング */
  gender?: Gender;
  
  /** シフト優先度でのフィルタリング */
  shift_priority?: ShiftPriority;
  
  /** ページ番号 */
  page?: number;
  
  /** ページサイズ */
  per_page?: number;
}